import { z } from 'zod';

import { ToolOutputSuccessSchema } from '#plugin-chat/lib/tool/output-types.ts';

export const SLIDE_TYPES = ['option', 'checkbox', 'confirmation'] as const;
export type SlideType = (typeof SLIDE_TYPES)[number];
/** How a slide collects a response: immediate single choice, or accumulated multi-select. */
export type SelectionMode = 'single' | 'multi';

/**
 * Single source of truth for the LLM-facing slide-type glossary and selection
 * behavior. The `Record<SlideType, …>` shape makes a new variant a compile
 * error here, so the glossary served to the model (and the `z.enum` parser
 * description) can never drift from the {@link SLIDE_TYPES} union. Both the
 * `<dl>` glossary in `AskUserQuestionTool` and the `type` field `.describe()`
 * below are generated from this record.
 */
export const SLIDE_TYPE_META: Record<SlideType, { label: string; blurb: string; selectionMode: SelectionMode }> = {
  option: {
    label: 'Single choice (disambiguation)',
    blurb: 'select exactly one (radio-style)',
    selectionMode: 'single',
  },
  checkbox: {
    label: 'Multiple selections',
    blurb: 'select one or more (chips)',
    selectionMode: 'multi',
  },
  confirmation: {
    label: 'Yes/No',
    blurb: 'yes/no with optional items list',
    selectionMode: 'single',
  },
};

/** Option within a slide - represents a selectable choice. */
const slideOptionSchema = z.object({
  id: z.string().describe('Unique identifier for this option (used in response)'),
  label: z.string().describe('Display text for the option (1-3 words)'),
  description: z
    .string()
    .optional()
    .describe('Longer explanation shown below label, include unless label is self-explanatory'),
});

export type SlideOption = z.infer<typeof slideOptionSchema>;
/**
 * Shared props for every slide renderer. Each slide variant extends this base,
 * so the selection callback is a single uniform array contract — single-select
 * leaves (option/confirmation) wrap their chosen `id` into `[id]` at the click
 * site rather than forcing the caller to reconcile scalar-vs-array at runtime.
 */
export interface BaseSlideProps {
  options: SlideOption[];
  selectedIds: string[];
  onOptionClick: (ids: string[]) => void;
  isStreaming?: boolean;
}

/**
 * Single slide in the stepper flow.
 *
 * Slide type determines rendering behavior:
 * - `option`: Radio-style selection (exactly one choice)
 * - `checkbox`: Multi-select with chips (one or more choices)
 * - `confirmation`: Yes/No with optional items preview
 */
const stepperSlideSchema = z.object({
  title: z.string().describe('Short slide header (2-5 words)'),
  type: z
    .enum(SLIDE_TYPES)
    .describe(
      `Slide interaction type: ${SLIDE_TYPES.map((type) => `"${type}" = ${SLIDE_TYPE_META[type].blurb}`).join(', ')}`,
    ),
  question: z
    .string()
    .describe(
      'Question or instruction text displayed to user. Should be self-contained: include up to one paragraph of relevant context.',
    ),
  options: z
    .array(slideOptionSchema)
    .min(2)
    .describe(
      'Available choices (2+ options). ' +
        'For confirmation type, use exactly 2 options: one for confirm (e.g., "Yes, proceed"), one for cancel (e.g., "Cancel").',
    ),
});

export type StepperSlide = z.infer<typeof stepperSlideSchema>;

/** Response from a single slide - the selected option ID(s). */
export interface SlideResponse {
  /** Selected option IDs (single for option/confirmation, multiple for checkbox) */
  selectedIds: string[];
}

/** Complete response from the stepper flow. */
export interface StepperResponse {
  /** Responses for each slide in order */
  slideResponses: SlideResponse[];
  /** Whether the user completed the flow (true) or cancelled (false) */
  completed: boolean;
}

/** Payload structure for stepper slides */
export interface StepperPayload {
  type: 'stepper';
  slideIndex: number;
  totalSlides: number;
  slide: StepperSlide;
}

export const AskUserQuestionOutputSchema = ToolOutputSuccessSchema.extend({
  /** User selections keyed by slide title. */
  data: z.record(z.string(), z.array(z.string())),
});

/** @public */
export type AskUserQuestionOutput = z.infer<typeof AskUserQuestionOutputSchema>;

export const AskUserQuestionInputSchema = z.object({
  slides: z
    .array(stepperSlideSchema)
    .min(1)
    .max(5)
    .describe(
      'Stepper slides (1-5). ' +
        'Use 1 slide for quick questions, confirmations, or simple choices. ' +
        'Use 2-5 slides for guided flows requiring multiple steps. ' +
        'Each slide is displayed one at a time with back navigation.',
    ),
});

export type AskUserQuestionInput = z.infer<typeof AskUserQuestionInputSchema>;
